/**
 * O FECHAMENTO do mês de uma congregação, só leitura.
 *
 * **Fase 5 do espelho**, aprovada em 12/10/2026, com a prévia do mês aberto.
 *
 * A marcação é a do notebook, copiada de `../adsa-financas/src/renderer/pages/Fechamento.tsx`,
 * com os passos de assinar fora: o que sobra é a conferência dos números e o histórico. Assinar
 * continua sendo no notebook, e a tela diz isso.
 *
 * **Duas telas numa só, e a diferença é gritada:** no mês assinado os números são a fotografia
 * que o notebook gravou; no mês aberto são a PRÉVIA, calculada agora, com a tarja e a marca
 * d'água no papel. Confundir os dois é exatamente o que a tarja existe para impedir.
 */
import { Fragment, useEffect, useRef, useState, type JSX } from 'react'
import { AtualizadoAs, useAtualizarSozinho } from '../componentes/AtualizadoAs'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type TelaDoFechamento
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { NOMES_DIRECAO, type DirecaoRepasse } from '../../../src/compartilhado/types'
import { competenciaPorExtenso, emReais, mesAnterior, mesSeguinte, quandoFoi } from '../dinheiro'
import {
  ehParaImprimir, enderecoDaCongregacao, enderecoDoFechamentoDaCongregacao, irPara
} from '../rotas'

/**
 * O que a tarja da prévia diz, por situação.
 *
 * **"Mês aberto" não serve para os três casos**, e foi o achado 4 da segunda rodada da auditoria,
 * em 13/10/2026: um mês que o Campo já travou aparecia como aberto, e quem lesse acharia que
 * ainda dá para lançar. São estados diferentes, e a tarja é onde a pessoa olha.
 *
 * `ASSINADO` não entra aqui: ele não tem tarja de prévia, tem a linha da assinatura.
 */
const COMO_ESTA_O_MES: Record<string, string> = {
  ABERTO: 'mês aberto',
  REABERTO: 'mês reaberto',
  TRAVADO: 'mês travado pelo Campo'
}

export function FechamentoDaCongregacao({
  eu,
  id,
  competenciaDoEndereco,
  aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const [dados, setDados] = useState<TelaDoFechamento | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const paraImprimir = ehParaImprimir()
  /* Só a PRÉVIA se atualiza sozinha: o mês assinado não muda. No papel, nunca. */
  const atualizacao = useAtualizarSozinho(!paraImprimir && dados?.e_previa !== false)
  const { rodada, marcarAtualizado } = atualizacao
  /* A busca de outra folha apaga a tela; a atualização da mesma folha, não. */
  const qualFolha = useRef('')

  useEffect(() => {
    let vale = true
    setErro(null)
    const folha = `${id}|${competenciaDoEndereco ?? ''}`
    if (qualFolha.current !== folha) setDados(null)
    qualFolha.current = folha

    servidor
      .fechamentoDaCongregacao(id, competenciaDoEndereco)
      .then((recebido) => {
        if (!vale) return
        setDados(recebido)
        marcarAtualizado()
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [id, competenciaDoEndereco, aoSair, rodada, marcarAtualizado])

  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDoFechamentoDaCongregacao(id, para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima
          eu={eu}
          aoSair={aoSair}
          voltarPara={{ endereco: enderecoDaCongregacao(id, mes), texto: 'Folhas' }}
        />
      )}

      <main
        className="folha folha-lancamento"
        data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}
        /* Acende a marca d'água PRÉVIA no `@media print`, como na prévia do Campo. */
        data-previa={dados?.e_previa === true ? 'sim' : 'nao'}
      >
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {dados.e_previa && !paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            <header className="cabecalho-da-congregacao">
              <h1>{dados.congregacao.nome}</h1>
            </header>

            {mes !== null && !paraImprimir && (
              <nav className="navegar-mes so-tela">
                <button aria-label="Mês anterior" onClick={() => andar(mesAnterior(mes))}>◀</button>
                <strong>{competenciaPorExtenso(mes)}</strong>
                <button aria-label="Mês seguinte" onClick={() => andar(mesSeguinte(mes))}>▶</button>
                <a
                  className={`baixar${dados.e_previa ? ' previa' : ''}`}
                  href={`/v1/painel/congregacoes/${encodeURIComponent(id)}/fechamento.pdf?competencia=${mes}`}
                >
                  {dados.e_previa ? 'Baixar a prévia em PDF' : 'Baixar o fechamento em PDF'}
                </a>
              </nav>
            )}

            <h2>FECHAMENTO – {dados.competencia_por_extenso.toUpperCase()}</h2>

            {dados.e_previa ? (
              <p className="tarja-da-previa">
                <strong>PRÉVIA</strong> · {COMO_ESTA_O_MES[dados.situacao] ?? 'mês aberto'} ·
                {' '}não assinado
              </p>
            ) : (
              <p className="situacao-do-campo">
                <span className="situacao FECHADO">fechado e assinado</span>{' '}
                {dados.assinatura?.assinado_por !== null && dados.assinatura !== null && (
                  <>
                    por {dados.assinatura.assinado_por}
                    {dados.assinatura.assinado_em !== null &&
                      ` em ${dataDoNotebook(dados.assinatura.assinado_em)}`}
                    {dados.assinatura.versao > 1 && ` · versão ${dados.assinatura.versao}`}
                  </>
                )}
              </p>
            )}

            <Numeros dados={dados} />

            {dados.ofertas_por_destino.length > 0 && <ForaDoCaixa dados={dados} />}

            <Repasses dados={dados} />

            {dados.e_previa && (dados.impede_fechar > 0 || dados.esperando_aprovacao.total > 0) && (
              <div className="falta-para-fechar">
                <h3>O que ainda falta</h3>
                <ul>
                  {dados.esperando_aprovacao.total > 0 && (
                    <li>
                      <strong>
                        {dados.esperando_aprovacao.total === 1
                          ? '1 saída aguarda aprovação'
                          : `${dados.esperando_aprovacao.total} saídas aguardam aprovação`}
                      </strong>
                      {dados.esperando_aprovacao.pela_conta_do_campo > 0 && (
                        <>
                          {', '}
                          {dados.esperando_aprovacao.pela_conta_do_campo === 1
                            ? '1 delas paga pela conta do Campo para esta congregação'
                            : `${dados.esperando_aprovacao.pela_conta_do_campo} delas pagas pela conta do Campo para esta congregação`}
                        </>
                      )}
                      . A congregação não assina com saída aguardando aprovação: quem aprova destrava,
                      aqui no painel, em Saídas esperando decisão, ou no notebook.
                    </li>
                  )}
                  {dados.impede_fechar > 0 && (
                    <li>
                      <strong>
                        {dados.impede_fechar === 1
                          ? '1 saída aprovada está sem o comprovante que a categoria exige'
                          : `${dados.impede_fechar} saídas aprovadas estão sem o comprovante que a categoria exige`}
                      </strong>
                      . Enquanto estiver assim, o mês não fecha no notebook.
                    </li>
                  )}
                </ul>
              </div>
            )}

            {dados.versoes.length > 0 && <Versoes dados={dados} />}

            <p className="explicacao">
              {dados.e_previa
                ? 'Assinar e travar o mês continua sendo no notebook. Esta tela é só para conferir.'
                : 'Esta é a fotografia que o notebook gravou na assinatura. O painel não a recalcula.'}
            </p>
          </>
        )}
      </main>
    </>
  )
}

/** O bloco "1. Conferir os números" do notebook, sem o que se digita. */
function Numeros({ dados }: { dados: TelaDoFechamento }): JSX.Element {
  const t = dados.totais

  /*
    Esta seção NÃO usa `folha-de-um-tipo`, e isso tem motivo: aquele invólucro põe
    `min-width: 740px` na tabela, para as folhas largas rolarem para o lado no celular. Esta
    tabela tem DUAS colunas e cabe num telefone; dentro dele, a coluna do dinheiro saía da tela
    e o primeiro retrato desta entrega mostrou os rótulos sem nenhum valor ao lado.
  */
  return (
    <section className="conta-do-fechamento">
      <table className="grade conta-da-previa">
        <tbody>
          <tr><th>Ofertas</th><td className="valor">{emReais(t.ofertas)}</td></tr>
          <tr><th>Dízimos</th><td className="valor">{emReais(t.dizimos)}</td></tr>
          <tr><th>Votos</th><td className="valor">{emReais(t.votos)}</td></tr>
          <tr>
            <th>Saídas do caixa</th>
            <td className="valor">− {emReais(t.saidas)}</td>
          </tr>
          <tr className="resultado">
            <th>SALDO DO MOVIMENTO</th>
            <td className="valor">{emReais(t.saldo)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  )
}

/**
 * O que passou pela congregação mas NÃO sai do caixa dela.
 *
 * A oferta com destino é do departamento; o cartão e a conta do Campo têm livro próprio. Somar
 * isso ao saldo daria um número que não é de ninguém, e é por isso que vem em bloco separado,
 * como no notebook.
 */
function ForaDoCaixa({ dados }: { dados: TelaDoFechamento }): JSX.Element {
  const t = dados.totais

  return (
    <section className="conta-do-fechamento">
      <p className="explicacao"><strong>Fora do caixa da congregação:</strong></p>
      <table className="grade conta-da-previa">
        <tbody>
          {dados.ofertas_por_destino.map((d) => (
            <tr key={d.departamento} className="por-categoria">
              <th>
                Oferta com destino: {d.departamento}{' '}
                <span className="explicacao">(vai para o departamento)</span>
              </th>
              <td className="valor">{emReais(d.valor)}</td>
            </tr>
          ))}
          {t.saidas_cartao !== 0 && (
            <tr className="por-categoria">
              <th>Pago pelo cartão da congregação</th>
              <td className="valor">{emReais(t.saidas_cartao)}</td>
            </tr>
          )}
          {t.saidas_campo !== 0 && (
            <tr className="por-categoria">
              <th>Pago pela conta do Campo</th>
              <td className="valor">{emReais(t.saidas_campo)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <th>CUSTO REAL DO MÊS <span className="explicacao">(caixa + cartão + conta do Campo)</span></th>
            <td className="valor">{emReais(t.custo_real)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  )
}

/**
 * O repasse que JÁ está no razão deste mês.
 *
 * **O painel não prevê o repasse**, e isso é decisão escrita: a regra mora em `regras_repasse` e
 * tem casos, e reimplementá-la aqui seria uma segunda regra de dinheiro para o mesmo Campo. O
 * repasse do mês que ainda não foi gerado nasce na assinatura, no notebook.
 */
/** "2026-10-18 10:00:00", como o notebook escreve, em "18/10/2026". */
const dataDoNotebook = (quando: string): string => {
  const [a, m, d] = quando.slice(0, 10).split('-')
  return a && m && d ? `${d}/${m}/${a}` : quando
}

function Repasses({ dados }: { dados: TelaDoFechamento }): JSX.Element {
  if (dados.repasses.length === 0) {
    return (
      <p className="explicacao">
        Nenhum repasse deste mês está no razão ainda.{' '}
        {dados.e_previa && 'Ele nasce quando o mês for assinado, no notebook.'}
      </p>
    )
  }

  /*
   * CALCULADO E PAGO, LADO A LADO, e o motivo quando os dois diferem. Decisão do Cristiano, de
   * 17/10/2026: a auditora pagou 7 de 20 centavos e a tela mostrava 20 como pago, enquanto o
   * total usava 7. O total continua sendo o que entrou no razão: o pago, quando houve pagamento.
   */
  const totalCalculado = dados.repasses.reduce((s, r) => s + r.valor_calculado, 0)
  return (
    <section className="conta-do-fechamento">
      <p className="explicacao"><strong>Repasse já lançado neste mês:</strong></p>
      <table className="grade conta-da-previa">
        <thead>
          <tr>
            <th></th>
            <th className="valor">Calculado</th>
            <th className="valor">Pago</th>
          </tr>
        </thead>
        <tbody>
          {dados.repasses.map((r) => (
            <Fragment key={r.id}>
              <tr>
                <th>
                  {r.direcao === 'SEDE_PARA_MATRIZ' ? 'À Matriz' : NOMES_DIRECAO[r.direcao as DirecaoRepasse] ?? 'Repasse'}{' '}
                  <span className="explicacao">({r.status.toLowerCase()})</span>
                </th>
                <td className="valor">{emReais(r.valor_calculado)}</td>
                <td className="valor">{r.valor_pago === null ? 'ainda não' : emReais(r.valor_pago)}</td>
              </tr>
              {r.estorno_do_pagamento && (
                <tr className="motivo-do-repasse">
                  <td colSpan={3} className="explicacao">
                    {r.valor_pago === null ? 'Pagamento estornado' : 'Um pagamento anterior foi estornado'} em{' '}
                    {dataDoNotebook(r.estorno_do_pagamento.em)}
                    {r.estorno_do_pagamento.por !== null && <>, por {r.estorno_do_pagamento.por}</>}.{' '}
                    {r.estorno_do_pagamento.motivo
                      ? <>Motivo: {r.estorno_do_pagamento.motivo}</>
                      : <strong className="aviso">Sem motivo escrito.</strong>}
                  </td>
                </tr>
              )}
              {r.valor_pago !== null && r.valor_pago !== r.valor_calculado && (
                <tr className="motivo-do-repasse">
                  <td colSpan={3} className={r.diferenca_sem_motivo ? 'aviso' : 'explicacao'}>
                    {r.diferenca_sem_motivo
                      ? 'Pago com valor diferente do calculado, sem motivo escrito.'
                      : `Pago diferente do calculado: ${r.motivo_valor_pago}`}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr className="subtotal">
            <th>Total</th>
            <td className="valor">{emReais(totalCalculado)}</td>
            <td className="valor">{emReais(dados.total_repasse_no_razao)}</td>
          </tr>
        </tbody>
      </table>
      <p className="explicacao">
        No total, o que conta é o que foi pago; o que ainda não foi pago entra pelo calculado.
      </p>
    </section>
  )
}

/** O histórico de versões. **Nenhuma é apagada**, e é por isso que ele existe. */
function Versoes({ dados }: { dados: TelaDoFechamento }): JSX.Element {
  return (
    <section className="folha-de-um-tipo">
      <h3>Versões deste fechamento</h3>
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>

      <table className="grade">
        <thead>
          <tr>
            <th className="rotulo">VERSÃO</th>
            <th className="rotulo">SITUAÇÃO</th>
            <th className="valor">SALDO</th>
            <th className="valor">REPASSE</th>
            <th className="rotulo">ASSINADO POR</th>
            <th className="rotulo">MOTIVO DA REABERTURA</th>
          </tr>
        </thead>
        <tbody>
          {dados.versoes.map((v) => (
            <tr key={v.versao}>
              <td className="impresso">{v.versao}</td>
              <td className="impresso">{v.status === 'FECHADO' ? 'Fechado' : 'Reaberto'}</td>
              <td className="valor manuscrito">{emReais(v.saldo)}</td>
              <td className="valor manuscrito">{emReais(v.total_repasse)}</td>
              <td className="impresso">
                {v.assinado_por ?? '—'}
                {v.assinado_em !== null && (
                  <span className="explicacao"> {quandoFoi(v.assinado_em, '')}</span>
                )}
              </td>
              <td className="impresso">{v.motivo_da_reabertura ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
